import { useEffect, useRef } from 'react';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { UserRepository } from '@/repositories/UserRepository';
import type { AuthProfileRow } from '@/repositories/UserRepository';
import { useAuth } from '@/store/useAppStore';
import { isValidProfileData, sanitizePermissions } from '@/lib/type-guards';
import { getInviteToken } from '@/utils/inviteTokenStorage';

interface UseAuthInitializationOptions {
  /**
   * Neutralise complètement l'amorçage. Utilisé pour une visite par lien public :
   * il n'y a pas de session à restaurer, et le profil synthétique posé par
   * usePublicLinkSession ne doit pas être écrasé.
   */
  enabled?: boolean;
}

export function useAuthInitialization({ enabled = true }: UseAuthInitializationOptions = {}) {
  const { session, setSession, setProfile, setLoading } = useAuth();

  // BUG-002 FIX: Utiliser un ref pour éviter les fetches multiples
  const profileFetchedForUserRef = useRef<string | null>(null);

  // Listen for auth changes
  useEffect(() => {
    if (!enabled) return;

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const { data: listener } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, session: Session | null) => {
        setSession(session);

        // Réinitialiser le ref si l'utilisateur se déconnecte
        if (event === 'SIGNED_OUT') {
          profileFetchedForUserRef.current = null;
          setProfile(null);
        }
      }
    );

    return () => listener?.subscription.unsubscribe();
  }, [enabled, setSession, setProfile]);

  // BUG-002 FIX: Fetch profile quand l'ID utilisateur change (pas la session entière)
  useEffect(() => {
    if (!enabled) return;

    const userId = session?.user?.id;

    const fetchProfile = async () => {
      // Pas de session = pas de profil
      if (!userId) {
        setProfile(null);
        setLoading(false);
        return;
      }

      // BUG-002 FIX: Éviter les fetches multiples pour le même utilisateur
      if (profileFetchedForUserRef.current === userId) {
        return;
      }

      // Vérifier le token d'invitation
      const tokenFromStorage = getInviteToken();
      const tokenFromUrl = new URLSearchParams(window.location.search).get('token');
      const hasInviteToken = tokenFromStorage || tokenFromUrl;

      if (hasInviteToken) {
        console.log('ℹ️ [AUTH] Token d\'invitation détecté, pas de vérification du profil');
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        // Erreur de lecture capturée ici (et non dans le catch « Unexpected ») :
        // même traitement qu'un profil absent ou invalide
        let profileData: AuthProfileRow | null = null;
        let error: unknown = null;
        try {
          profileData = await UserRepository.getAuthProfile(userId);
        } catch (fetchError) {
          error = fetchError;
        }

        if (error || !profileData || !isValidProfileData(profileData)) {
          console.error('[AUTH] Error fetching profile:', error);

          // BUG-004 FIX: Signout d'abord, puis naviguer
          // Les state updates après navigation ne sont pas nécessaires
          // car la page va être complètement rechargée
          await supabase.auth.signOut();

          // Navigation immédiate - le composant sera démonté
          window.location.href = '/unauthorized';
          return;
        }

        // Marquer le profil comme fetché pour cet utilisateur
        profileFetchedForUserRef.current = userId;

        setProfile({
          id: profileData.id,
          role: profileData.role,
          company_id: profileData.company_id,
          status: profileData.status,
          permissions: sanitizePermissions(profileData.permissions),
          default_expense_type_id: profileData.default_expense_type_id,
          default_subcategory_id: profileData.default_subcategory_id,
        });
      } catch (error) {
        console.error('[AUTH] Unexpected error:', error);
        await supabase.auth.signOut();
        window.location.href = '/unauthorized';
        return;
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [enabled, session?.user?.id, setProfile, setLoading]); // BUG-002 FIX: Dépendre de l'ID, pas de session
}
